<template>
  <div v-if="company" class="container-fluid pb-6">
    <DashboardNavigationComponent
      :title="
        company.registry_code
          ? `${company.name} (${company.registry_code})`
          : company.name
      "
      :devices-total="registryDevicesStore.registryDevices.length"
      :power-total="registryDevicesStore.totalDevicePower"
      :is-admin="is_admin"
      :show-income-estimate="show_income_estimate"
      :admin-id="company.id"
      :pending-payouts="pendingPayouts"
      :can-submit-invoice="pendingPayouts.length > 0"
      :certificates-count="portfolioItemsStore.totalAvailableGOs"
      :projected-production="projectedProduction"
      :is-loading="isLoading"
      :devices-query-params="devicesQueryParams"
      :is-single-company="true"
    />

    <FollowUpSection :company="company" :account-id="account_id" />

    <!-- Tab content -->
    <div class="tab-content">
      <KeepAlive>
        <BigProducerCertificatesTab
          v-if="activeTab === 'certificates'"
          :company
          :isAdmin="is_admin"
        />
        <BigProducerInvoiceHistoryTab
          v-else-if="activeTab === 'invoice-history'"
          :company
          :isAdmin="is_admin"
          :pendingInvoiceItems
          :defaultInvoiceNumber
          :monthSplit
          :initialTab="invoiceManagementInitialTab"
          @invoice-requested="handleInvoiceRequested"
        />
        <BigProducerTradingStrategyTab
          v-else-if="activeTab === 'trading-strategy'"
          :company
          :registryDevices="registryDevicesStore.registryDevices"
        />
        <BigProducerSettingsTab
          v-else-if="activeTab === 'settings'"
          :company
          :isAdmin="is_admin"
        />
      </KeepAlive>
    </div>

    <div v-if="incompleteGenerators.length !== 0">
      <GeneratorCardComponent
        v-for="generator in incompleteGenerators"
        :key="generator.id"
        :generator="generator"
        :account_id="account_id"
        :title="
          $t('dashboard.companyData.device_contract', {
            deviceName: generator.name,
          })
        "
        :generatorDocuments="filterGeneratorDocuments(generator.id ?? 0)"
        :is_admin="is_admin"
      />
    </div>
  </div>
  <loading-overlay-component v-if="isLoading"></loading-overlay-component>
</template>

<script setup lang="ts">
import { pendingPayoutsByPayoutCurrency } from "@/composables/money/invoiceUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useProjections } from "@/composables/useProjections";
import { useQueryManager } from "@/composables/useQueryManager";
import { useRegistryDevicesStore } from "@/composables/useRegistryDevicesStore";
import BigProducerCertificatesTab from "@/dashboard/BigProducerCertificatesTab.vue";
import BigProducerInvoiceHistoryTab from "@/dashboard/BigProducerInvoiceHistoryTab.vue";
import BigProducerSettingsTab from "@/dashboard/BigProducerSettingsTab.vue";
import BigProducerTradingStrategyTab from "@/dashboard/BigProducerTradingStrategyTab.vue";
import FollowUpSection from "@/dashboard/common/FollowUpSection.vue";
import DashboardNavigationComponent from "@/dashboard/DashboardNavigationComponent.vue";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import { calculateTotalProjection } from "@/utils/projections";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import LoadingOverlayComponent from "../components/common/LoadingOverlayComponent.vue";
import type {
  Certificate,
  Company,
  Document,
  Generator,
  InvoiceItem,
  MonthSplit,
} from "../types";
import GeneratorCardComponent from "./common/GeneratorCardComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const props = withDefaults(
  defineProps<{
    company: Company;
    account_id: number;
    is_admin: boolean;
    show_income_estimate?: boolean;
  }>(),
  { show_income_estimate: false }
);

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler: handleSuccess } =
  useGlobalHandlers();
const isLoading = ref(false);
const registryDevicesStore = useRegistryDevicesStore();
const certificates = ref<Certificate[]>([]);
const pendingInvoiceItems = ref<InvoiceItem[]>([]);
const defaultInvoiceNumber = ref("");
const monthSplit = ref<MonthSplit | null>(null);
const portfolioItemsStore = usePortfolioItemsStore();
const devicesQueryParams = ref<Record<string, string>>({
  tab: "settings",
  "settings-active-tab": "settings",
  "settings-management-active-tab": "devices",
});

const { getQueryValue, parsers } = useQueryManager();
const activeTab = getQueryValue<string>(
  "tab",
  parsers.stringWithDefault("certificates")
);
const invoiceManagementInitialTab = getQueryValue<string>(
  "invoice-management-active-tab",
  parsers.stringWithDefault("")
);

const pendingPayouts = computed(() =>
  pendingPayoutsByPayoutCurrency(pendingInvoiceItems.value, [props.company])
);

function isIncompleteGenerator(generator: Generator) {
  if (!generator.id) {
    return true;
  }
  const generatorDocuments = filterGeneratorDocuments(generator.id);
  if (
    generatorDocuments.some(
      (document: Document) => document.status !== "completed"
    )
  ) {
    return true;
  }
  return !generator.has_registered;
}

const incompleteGenerators = computed(() => {
  return props.company.generators.filter((generator: Generator) =>
    isIncompleteGenerator(generator)
  );
});

const filterGeneratorDocuments = (generatorId: number) => {
  return props.company.documents.filter(
    (doc: Document) => doc.generator_id === generatorId
  );
};

async function handleInvoiceRequested() {
  handleSuccess(
    t("dashboard.companyData.invoice_submission_success"),
    t("dashboard.companyData.invoice_paid_within_7_days")
  );
  pendingInvoiceItems.value = [];
  monthSplit.value = null;
  activeTab.value = "";
}

async function getPendingInvoiceItems() {
  try {
    const response = await goSolidApi.get(
      `/api/company/${props.company.id}/pending-invoice-items`
    );
    if (response.status === 200) {
      pendingInvoiceItems.value = response.data.invoice_items;
      defaultInvoiceNumber.value = response.data.default_invoice_number;
      monthSplit.value = response.data.month_split;
    } else {
      throw new Error(
        t("dashboard.companyData.fetch_pending_invoice_items_failed")
      );
    }
  } catch (error) {
    globalErrorHandler(error);
  }
}

async function fetchCertificates() {
  try {
    const response = await goSolidApi.get(
      `/api/company/certificates/${props.company.id}`
    );
    if (response.status === 200) {
      certificates.value = response.data.certificates;
    } else {
      throw new Error(t("dashboard.companyData.fetch_certificates_failed"));
    }
  } catch (error) {
    globalErrorHandler(error);
  }
}

const { projections, fetchProjections } = useProjections({
  onError: globalErrorHandler,
});

const projectedProduction = computed(() => {
  return calculateTotalProjection(projections.value);
});

async function loadCompanyData() {
  isLoading.value = true;
  try {
    portfolioItemsStore.setAllItems([]);

    await Promise.all([
      fetchCertificates(),
      registryDevicesStore.fetchRegistryDevices(props.company.id),
      getPendingInvoiceItems(),
      fetchProjections(props.company.id),
      portfolioItemsStore
        .fetchItems(props.company.id!)
        .catch(globalErrorHandler),
    ]);
  } finally {
    isLoading.value = false;
  }
}

watch(
  () => props.company.id,
  async (newCompanyId, oldCompanyId) => {
    if (newCompanyId !== oldCompanyId) {
      await loadCompanyData();
    }
  }
);

onMounted(async () => {
  await loadCompanyData();
});
</script>

<style scoped lang="scss">
.container-fluid {
  text-align: left;
  max-width: 1400px;
  margin: 0 auto;
}
</style>
